
import * as angular from "angular";
import * as moment from "moment";

import { PlotService } from "./plot.service";
import { MapService } from "./map.service";
import { IPlot } from "@interfaces/IPlot";
import { IJourney } from "@interfaces/IJourney";
import { ConstantsService } from "./constantsService";

export interface IGoogleRoadDataSnappedPointLocation {
    lat: number;
    lng: number;
}

export interface IGoogleRoadDataSnappedPoint {
    Location: IGoogleRoadDataSnappedPointLocation;
    OriginalIndex: number;
    PlaceId: string;
    SpeedLimit: number;
}

export interface IGoogleRoadDataSpeedLimit {
    placeId: string;
    speedLimit: number;
    units: string;
}

export interface IGoogleRoadData {
    snappedPoints: IGoogleRoadDataSnappedPoint[];
    speedLimits: IGoogleRoadDataSpeedLimit[];
    warningMessage: string;
}

export interface IGoogleElevationData {
}

export class GoogleService {
    apiUrl: string

    static $inject = ["$http", "$q", "$document", "plotService", "mapService", "constantsService"];

    constructor(
        private $http: angular.IHttpService,
        private $q: angular.IQService,
        private $document: angular.IDocumentService,
        private plotService: PlotService,
        private mapService: MapService,
        private readonly constantsService: ConstantsService) {
        this.apiUrl = this.constantsService.getApiUrl();
    }

    // There is likely a better place for this
    boundsGreatBritain = {
        ne: {
            lat: 60.041972430353184,
            lng: 7.55572621875001
        } as google.maps.LatLngLiteral,
        sw: {
            lat: 49.60950162107379,
            lng: -15.25189096874999
        } as google.maps.LatLngLiteral
    };

    snapToRoads(latlngs: google.maps.LatLng[]): angular.IPromise<google.maps.LatLng[]> {

        latlngs.forEach(latlng => {
            console.log(`${latlng.lat()}, ${latlng.lng()}|`);
        });

        const requestArray: angular.IPromise<google.maps.LatLng[]>[] = [];

        const closePlotsArrays: google.maps.LatLng[][] = [];
        closePlotsArrays.push([]);
        let pathIndex = 0;

        let previousPlot: google.maps.LatLng;

        for (let i = 0; latlngs.length > i; i++) {
            let triggerProcess = false;
            const currentPlot = latlngs[i];

            const lastIndex = latlngs.length - 1;

            if (previousPlot) {
                const distance = this.getDistanceInMetres(currentPlot, previousPlot);
                if (distance < 300 && i !== lastIndex) {
                    closePlotsArrays[pathIndex].push(currentPlot);
                }
                else {
                    triggerProcess = closePlotsArrays[pathIndex].length > 0;
                }
            }

            if (triggerProcess || (lastIndex === i && closePlotsArrays[pathIndex].length > 0)) {
                requestArray.push(this.googleSnapToRoads(angular.copy(closePlotsArrays[pathIndex])));
            }

            if (lastIndex !== i && triggerProcess) {
                closePlotsArrays.push([]);
                pathIndex++;
            }

            previousPlot = currentPlot;
        }

        return this.$q.all(requestArray).then((requests) => {
            for (let i = 0; requests.length > i; i++) {
                const closePlots = closePlotsArrays[i];
                const snappedPlots = requests[i];
                if (snappedPlots.length === 0) {
                    continue;
                }
                const startIndex = latlngs.indexOf(closePlots[0]);
                const endIndex = latlngs.indexOf(closePlots[closePlots.length - 1]) + 1;

                latlngs.splice(startIndex, endIndex - startIndex, ...snappedPlots);
            }

            return latlngs;
        })
            .catch(e => {
                console.error("snap to roads error", e);
                return latlngs;
            });
    }

    getPlaces(searchTerm: string): angular.IPromise<google.maps.places.PlaceResult[]> {
        // We cannot use places for now
        return this.$q.resolve([]);

        // Place service requires a div to render to
        //var element = angular.element("<div></div>");
        //const placesService = new google.maps.places.PlacesService(element[0] as HTMLDivElement);
        //const deferred = this.$q.defer<google.maps.places.PlaceResult[]>();

        //// For now [until we have options that cover this] we will only specify UK results
        //placesService.textSearch(
        //    {
        //        query: `${searchTerm}`,
        //        bounds: new google.maps.LatLngBounds(this.boundsGreatBritain.sw, this.boundsGreatBritain.ne)
        //    },
        //    (results, status) => {

        //        // To prevent a leak we need to remove this element as soon as we have finished searching
        //        element.remove();

        //        if (status === google.maps.places.PlacesServiceStatus.OK || status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        //            deferred.resolve(results);
        //        } else {
        //            deferred.reject(status);
        //        }
        //    });

        //return deferred.promise;
    }

    getAutocompletePredictions(searchTerm: string): angular.IPromise<google.maps.places.QueryAutocompletePrediction[]> {
        const autoCompleteService = new google.maps.places.AutocompleteService();
        const deferred = this.$q.defer<google.maps.places.QueryAutocompletePrediction[]>();

        autoCompleteService.getQueryPredictions(
            { input: searchTerm },
            (result, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {

                    deferred.resolve(result);
                } else {
                    deferred.reject(status);
                }
            });

        return deferred.promise;
    }


    snapAndInterpolatePath(points: google.maps.LatLng[]): angular.IPromise<google.maps.LatLng[]> {
        const maxLatLngsPerRequest = 20;

        var interpolatedPath: google.maps.LatLng[] = [];

        let promiseToWaitOn: angular.IPromise<google.maps.DirectionsResult> = this.$q.when(null);
        const requestPool: angular.IPromise<google.maps.DirectionsResult>[] = [];

        // We remove 1 from the length as our end and start points need to overlap to prevent a gap
        const numRequests = Math.ceil((points.length - 1) / maxLatLngsPerRequest);

        for (let i = 0; i < numRequests; i++) {
            const startIndex = i * maxLatLngsPerRequest;
            const endIndex = startIndex + maxLatLngsPerRequest;

            const requestPoints = points.slice(startIndex, endIndex);
            promiseToWaitOn = promiseToWaitOn.then(() => {

                var directionsPromise = this.getDirections(
                    requestPoints[0],
                    requestPoints[requestPoints.length - 1],
                    requestPoints.length > 2 ? requestPoints.slice(1, -1) : null);

                directionsPromise.then(result => {
                    interpolatedPath = interpolatedPath.concat(result.routes[0].overview_path);
                });

                return directionsPromise;
            });

            requestPool.push(promiseToWaitOn);
        }

        return this.$q.all(requestPool).then(() => {
            return interpolatedPath;
        });
    }

    getDirections(from: google.maps.LatLng, to: google.maps.LatLng, wayPoints?: google.maps.LatLng[], optimizeWaypoints = false): angular.IPromise<google.maps.DirectionsResult> {
        if (!wayPoints) {
            return this.getDirectionsSimple(from, to);
        }

        const directionsService = new google.maps.DirectionsService();

        const request: google.maps.DirectionsRequest = {
            origin: from,
            destination: to,
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.IMPERIAL,
            provideRouteAlternatives: true,
            optimizeWaypoints: optimizeWaypoints,
            waypoints: []
        };

        if (wayPoints) {
            wayPoints.forEach(waypoint => {
                request.waypoints.push({
                    location: waypoint,
                    stopover: true // true with multiple waypoints will optimise the route
                });
            });
        }

        const deferred = this.$q.defer<google.maps.DirectionsResult>();

        directionsService.route(request, (result, status) => {
            if (status === google.maps.DirectionsStatus.OK) {
                deferred.resolve(result);
            }
            else {
                deferred.reject(status);
            }
        });

        return deferred.promise;
    }

    getDirectionsSimple(from: google.maps.LatLng, to: google.maps.LatLng): angular.IPromise<google.maps.DirectionsResult> {
        return this
            .$http({
                url: "/api/google/getDirections",
                method: "POST",
                data: { From: [from.lat(), from.lng()], To: [to.lat(), to.lng()] }
            })
            .then((result: angular.IHttpPromiseCallbackArg<google.maps.DirectionsResult>) => {
                const directions = result.data;

                directions.routes.forEach(route => {
                    route.legs.forEach(leg => {
                        leg.steps.forEach((step: any) => {
                            step.path = this.decodePolyline(step.polyline.points);
                        });
                    });
                });


                return result.data;
            });
    }

    private decodePolyline(encoded): google.maps.LatLng[] {
        if (!encoded) {
            return [];
        }
        const poly: google.maps.LatLng[] = [];
        let index = 0;
        const len = encoded.length;
        let lat = 0;
        let lng = 0;

        while (index < len) {
            let b;
            let shift = 0;
            let result = 0;

            do {
                b = encoded.charCodeAt(index++) - 63;
                result = result | ((b & 0x1f) << shift);
                shift += 5;
            } while (b >= 0x20);

            const dlat = (result & 1) != 0 ? ~(result >> 1) : (result >> 1);
            lat += dlat;

            shift = 0;
            result = 0;

            do {
                b = encoded.charCodeAt(index++) - 63;
                result = result | ((b & 0x1f) << shift);
                shift += 5;
            } while (b >= 0x20);

            const dlng = (result & 1) != 0 ? ~(result >> 1) : (result >> 1);
            lng += dlng;

            const p = new google.maps.LatLng(lat / 1e5, lng / 1e5);

            poly.push(p);
        }
        return poly;
    }

    flattenLegs(legs: google.maps.DirectionsLeg[]): google.maps.DirectionsLeg {
        //console.log("flattenLegs", legs);

        if (!legs || legs.length === 0) {
            return null;
        }

        if (legs.length === 1) {
            return legs[0];
        }

        const returnLeg = angular.copy(legs[0]);

        for (let i = 1; i < legs.length; i++) {
            const leg = legs[i];

            // Todo: Flesh this out
            returnLeg.distance.value += leg.distance.value;
            returnLeg.duration.value += leg.duration.value;

            //console.warn("distance:", returnLeg.distance.value, "duration", returnLeg.duration.value);
        }

        this.updateDistanceText(returnLeg.distance);
        this.updateDurationText(returnLeg.duration);

        //console.error("distance:", returnLeg.distance.text, returnLeg.distance.value, "duration", returnLeg.duration.text, returnLeg.duration.value);

        return returnLeg;
    }

    updateDistanceText(distance: google.maps.Distance) {
        distance.text = (Math.round(distance.value * 0.000621371 * 10) / 10).toString() + " mi";
    }

    updateDurationText(duration: google.maps.Duration) {
        const durationMoment: any = moment.duration(duration.value * 1000);

        if (duration.value >= 60 * 1000) {
            duration.text = durationMoment.format("h [hours], m [mins]");
        } else {
            duration.text = durationMoment.format("m [mins]");
        }
    }

    updatePlotsElevation(plots: IPlot[]): angular.IPromise<google.maps.ElevationResult[]> {
        const latLngList = this.mapService.convertPlotsToGoogleLatLng(plots);

        const elevationPromise = this.getElevationForLatLngs(latLngList);

        elevationPromise.then(elevationResults => {
            for (let i = 0; i < plots.length; i++) {
                plots[i].elevation = elevationResults[i].elevation;
            }
        });

        return elevationPromise;
    }

    getElevationForLatLngs(latLngs: google.maps.LatLng[]): angular.IPromise<google.maps.ElevationResult[]> {

        const elevationService = new google.maps.ElevationService();

        // Convert our latlngs into an appropriate format for the roads
        const maxLatLngsPerRequest = 500; // 512
        const requestArray: angular.IPromise<google.maps.ElevationResult[]>[] = [];

        for (let i = 0; i < Math.ceil(latLngs.length / maxLatLngsPerRequest); i++) {
            const requestLatLngs = latLngs.slice(i * maxLatLngsPerRequest, (i + 1) * maxLatLngsPerRequest);

            const deferred = this.$q.defer<google.maps.ElevationResult[]>();

            const elevationRequest: google.maps.PathElevationRequest = {
                path: requestLatLngs,
                samples: requestLatLngs.length
            };

            elevationService.getElevationAlongPath(
                elevationRequest,
                (results: google.maps.ElevationResult[], status: google.maps.ElevationStatus) => {
                    if (status === google.maps.ElevationStatus.OK) {
                        deferred.resolve(results);
                    } else {
                        deferred.reject(status);
                    }
                });

            requestArray.push(deferred.promise);
        }

        return this.$q
            .all(requestArray)
            .then(allElevationResults => {

                var results: google.maps.ElevationResult[] = [];

                allElevationResults.forEach(elevationResults => {
                    results.push.apply(results, elevationResults);
                });

                return results;
            })
            .catch(() => {
                console.warn("Elevation request error");
                return [];
            });
    }

    getRouteMapUrl(latlngs: google.maps.LatLng[], polylineColor: string = null): ng.IPromise<string> {
        const startLat = latlngs[0].lat();
        const startLng = latlngs[0].lng();

        const endIndex = latlngs.length - 1;
        const endLat = latlngs[endIndex].lat();
        const endLng = latlngs[endIndex].lng();

        const markers = `markers=color:0x209058|${startLat},${startLng}&markers=color:0xcf4946|${endLat},${endLng}`;

        return this.getPolylineMapUrl(latlngs, polylineColor || "0x00426aff", markers, "278x173");
    }

    getPolylineMapUrl(latlngs: google.maps.LatLng[], polylineColor: string, markers: string, size = "278x133"): ng.IPromise<string> {

        const encodedPolyline = google.maps.geometry.encoding.encodePath(latlngs as any);

        const path = `path=weight:3|color:${polylineColor}|enc:${encodedPolyline}`;
        const partialUrl = `${markers}&${path}`;

        return this.makeGoogleMapsImageRequest(partialUrl, size);
    }

    getMapUrl(center: google.maps.LatLng, zoom = 12, size = "278x133") {
        const partialUrl = `center=${center.lat()},${center.lng()}&zoom=${zoom}&style=feature:all|element:labels|visibility:off&style=feature:road|element:labels|visibility:on`;

        return this.makeGoogleMapsImageRequest(partialUrl, size);
    }


    private getDistanceInMetres(plot1: google.maps.LatLng, plot2: google.maps.LatLng): number {
        return this.mapService.getDistance(plot1.lat(), plot1.lng(), plot2.lat(), plot2.lng(), true);
    }

    private toRad(deg): number {
        return deg * (Math.PI / 180);
    }

    private googleSnapToRoads(latlngs: google.maps.LatLng[]): angular.IPromise<google.maps.LatLng[]> {

        const requestArray = this.chunkLatLngStringRequests(latlngs, 100, (latLngString: string) => {
            console.log("googleSnapToRoads", latLngString);
            return this.sendSnapToRoadsRequest(latLngString);
        });

        return requestArray.then((response) => {
            const snappedLatLngs: google.maps.LatLng[] = [];

            response.forEach(roadData => {
                if (roadData.snappedPoints) {
                    roadData.snappedPoints.forEach(p => {
                        snappedLatLngs.push(new google.maps.LatLng(p.Location.lat, p.Location.lng));
                    });
                }
            });

            return snappedLatLngs;
        });
    }

    private makeGoogleMapsImageRequest(partialurl: string, size = "278x133"): ng.IPromise<string> {
        const key = "AIzaSyDhlFpGkr2YjeBNKf-GWNCUgGf-QA9XMB0";
        const host = "https://maps.googleapis.com/maps/api/staticmap";

        const fullUrl = `${host}?key=${key}&size=${size}&${partialurl}`;

        return this.$http(
            {
                method: "GET",
                url: `/api/google/signurl?url=${encodeURIComponent(fullUrl)}`
            })
            .then((response: angular.IHttpPromiseCallbackArg<string>) => {
                return response.data;
            });
    }

    private chunkLatLngRequests<T>(latLngs: google.maps.LatLng[], maxLatLngsPerRequest: number, request: (latLngs: number[][]) => angular.IPromise<T>): angular.IPromise<T[]> {
        const requestArray: angular.IPromise<T>[] = [];

        while (latLngs.length > 0) {
            const subList = latLngs.splice(0, maxLatLngsPerRequest);

            const latLngList: number[][] = [];
            for (let i = 0; i < subList.length; i++) {
                latLngList.push([
                    Number(subList[i].lat().toPrecision(6)),
                    Number(subList[i].lng().toPrecision(6))
                ]);
            }

            requestArray.push(request(latLngList));
        }

        return this.$q.all(requestArray);
    }

    private chunkLatLngStringRequests<T>(latLngs: google.maps.LatLng[], maxLatLngsPerRequest: number, request: (latLngString: string) => angular.IPromise<T>): angular.IPromise<T[]> {
        const requestArray: angular.IPromise<T>[] = [];

        while (latLngs.length > 0) {
            const subList = latLngs.splice(0, maxLatLngsPerRequest);
            let latLngString = "";

            for (let i = 0; i < subList.length; i++) {
                const latLng = subList[i];

                if (latLngString.length) {
                    latLngString += "|";
                }

                latLngString += latLng.toUrlValue();
            }

            requestArray.push(request(latLngString));
        }

        return this.$q.all(requestArray);
    }

    private sendSnapToRoadsRequest(latLngString: string, interpolate: boolean = true) {
        return this.$http(
            {
                url: "https://roads.googleapis.com/v1/snapToRoads",
                method: "GET",
                params: {
                    key: "hw/iVd9EGICXMSKp1fcTl9fBdUJf7ZBwWbckurpHHdDQ0OF7X4IlcK+YQMsRn/m/=",
                    interpolate: interpolate,
                    path: latLngString
                }
            })
            .then((roadData: angular.IHttpPromiseCallbackArg<IGoogleRoadData>) => {
                return roadData.data;
            });
    }
}
